import { useNavigate } from "react-router-dom";
import { useCallback, useMemo } from "react";
import useFilters from "recoilStore/useFilters";
import useCurrentFilterset from "queries/Reports/Baseline/useCurrentFilterset";

// components
import TableLoading from "components/molecules/TableLoading";
import SearchBar from "components/organisms/SearchBar/SearchBar";
import { ErrorPage } from "pages/ErrorState";
import useBuildingsPageState from "recoilStore/useBuildingsPageState";
import useTableState from "recoilStore/Table/useTableState";
import { TABLE_IDS } from "utils/constants";
import { FiltersetBasedEmissionIntensityMap } from "components/organisms/Charts";

import { Table, TableColumn } from "../../components/molecules/Table";
import { FilterLabelCards } from "../../components/organisms/FiltersSidebar/FilterLabelCards";
// helpers
import { BuildingsTableData } from "./helpers";

// styling
import "./Buildings.scss";
import { BuildingsData } from "./constants";

interface BuildingsProps {
    buildingsData: BuildingsData;
}

function Buildings({ buildingsData }: BuildingsProps) {
    const {
        error,
        isLoading,
        selectedBuildingRows,
        setSelectedBuildingRows,
        formattedTableViewColumns,
        tableViewTableData,
    } = buildingsData;

    const { numberOfFilters } = useFilters();

    if (error) return <ErrorPage />;
    if (isLoading) return <TableLoading />;

    return (
        <div className="buildings-page">
            {numberOfFilters !== 0 && <FilterLabelCards />}
            <ViewBody
                tableColumns={formattedTableViewColumns}
                tableData={tableViewTableData}
                selectedRows={selectedBuildingRows}
                setSelectedRows={setSelectedBuildingRows}
            />
        </div>
    );
}
interface ViewBodyProps {
    tableColumns: TableColumn[];
    tableData: BuildingsTableData[];
    selectedRows: BuildingsTableData[];
    setSelectedRows: React.Dispatch<React.SetStateAction<BuildingsTableData[]>>;
}

function ViewBody({ tableColumns, tableData, selectedRows, setSelectedRows }: ViewBodyProps) {
    const { selectedFiltersetUid, isLoading: isLoadingFilterset } = useCurrentFilterset();
    const { pageState } = useBuildingsPageState();
    const navigate = useNavigate();
    const { setResetSearchTrigger, setSearchBarText, setTablePage } = useTableState(TABLE_IDS.BUILDINGS);

    const resetSearchBar = useCallback(() => {
        setResetSearchTrigger();
        setSearchBarText("");
        setTablePage(0);
    }, [setResetSearchTrigger, setSearchBarText, setTablePage]);

    const handleBuildingRowClick = useCallback(
        (row: BuildingsTableData) => {
            const uid = row.buildingModelUid;
            if (uid) {
                resetSearchBar();
                navigate(`/buildings/${uid}`);
            }
        },
        [resetSearchBar, navigate]
    );

    const convertedTableData = useMemo(
        () =>
            tableData.map((row) => ({
                ...row,
                lastUpdated: new Date(row.lastUpdated),
            })),
        [tableData]
    );

    return (
        <div className="view-body">
            {pageState.view === "table" && (
                <div>
                    <SearchBar tableId={TABLE_IDS.BUILDINGS} />
                    <Table
                        columns={tableColumns}
                        dataSource={convertedTableData}
                        selectedRows={selectedRows}
                        setSelectedRows={setSelectedRows}
                        onRowClick={handleBuildingRowClick}
                        parentComponentWithSearchBarId={TABLE_IDS.BUILDINGS}
                        getRowHref={(row) => `/buildings/${row.buildingModelUid}`} // This is required for allowing right click in table
                    />
                </div>
            )}
            {pageState.view === "map" && !isLoadingFilterset && selectedFiltersetUid && (
                <FiltersetBasedEmissionIntensityMap
                    includeHeader={true}
                    groupByMetric="building"
                    filtersetUid={selectedFiltersetUid}
                    reportedVsPublished="reported"
                />
            )}
        </div>
    );
}

export default Buildings;
